// src/pages/api/api.js
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    Input, Tag, Empty, Space, Button, Tooltip, message,
} from 'antd';
import {
    SearchOutlined, CopyOutlined, CheckOutlined,
    ApiOutlined, SafetyCertificateOutlined, ReloadOutlined,
} from '@ant-design/icons';

import { endpointGroups, endpoints } from '../../constants/apiEndpoints';

import ApiNav from '../../components/layout/Apitopnav'

/* -------------------------------------------------------------- */
/*  Helpers                                                       */
/* -------------------------------------------------------------- */

const METHOD_COLOR = {
    GET: 'green',
    POST: 'blue',
    PUT: 'orange',
    PATCH: 'gold',
    DELETE: 'red',
};

function MethodTag({ method }) {
    if (!method) return null;
    return (
        <Tag color={METHOD_COLOR[method] || 'default'} style={{ margin: 0, fontWeight: 700 }}>
            {method}
        </Tag>
    );
}

function CopyButton({ text, intl }) {
    const [copied, setCopied] = useState(false);

    const onCopy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            message.success(intl.formatMessage({ id: 'api.copied', defaultMessage: 'Copied' }));
            setTimeout(() => setCopied(false), 1500);
        } catch {
            message.error(intl.formatMessage({ id: 'api.copyError', defaultMessage: 'Copy failed' }));
        }
    };

    return (
        <Tooltip title={intl.formatMessage({ id: 'api.copy', defaultMessage: 'Copy' })}>
            <Button
                type="text"
                size="small"
                icon={copied ? <CheckOutlined /> : <CopyOutlined />}
                onClick={onCopy}
            />
        </Tooltip>
    );
}

/* -------------------------------------------------------------- */
/*  Endpoint card                                                 */
/* -------------------------------------------------------------- */

function EndpointCard({ ep, intl }) {
    const hasPath = Boolean(ep.path);

    return (
        <div className="api__endpoint" id={ep.id}>
            <div className="api__endpoint-head">
                <Space size={8} wrap>
                    <MethodTag method={ep.method} />
                    {hasPath && (
                        <code className="api__endpoint-path">
                            {ep.path}
                            <CopyButton text={ep.path} intl={intl} />
                        </code>
                    )}
                </Space>
                <h3 className="api__endpoint-title">{ep.title}</h3>
            </div>

            {ep.description && (
                <p className="api__endpoint-desc">{ep.description}</p>
            )}

            {/* Documentation-style block */}
            {ep.type === 'documentation' && ep.content && (
                <pre className="api__endpoint-doc">{ep.content}</pre>
            )}

            {/* Parameters */}
            {Array.isArray(ep.parameters) && ep.parameters.length > 0 && (
                <div className="api__endpoint-section">
                    <h4 className="api__endpoint-sub">Parameters</h4>
                    <table className="api__table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Required</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ep.parameters.map((p) => (
                                <tr key={p.name}>
                                    <td><code>{p.name}</code></td>
                                    <td>{p.type}</td>
                                    <td>{p.required ? 'yes' : 'no'}</td>
                                    <td>
                                        {p.description || '—'}
                                        {p.default !== undefined && (
                                            <span className="api__default"> (default: <code>{String(p.default)}</code>)</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Headers */}
            {Array.isArray(ep.headers) && ep.headers.length > 0 && (
                <div className="api__endpoint-section">
                    <h4 className="api__endpoint-sub">Headers</h4>
                    <table className="api__table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Required</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ep.headers.map((h) => (
                                <tr key={h.name}>
                                    <td><code>{h.name}</code></td>
                                    <td>{h.required ? 'yes' : 'no'}</td>
                                    <td>
                                        {h.description || '—'}
                                        {h.value && (
                                            <span className="api__default"> → <code>{h.value}</code></span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Success response */}
            {ep.successResponse?.example && (
                <div className="api__endpoint-section">
                    <h4 className="api__endpoint-sub">
                        Success · {ep.successResponse.code || 200}
                    </h4>
                    <pre className="api__code">
                        {ep.successResponse.example}
                    </pre>
                </div>
            )}

            {/* Error responses */}
            {Array.isArray(ep.errorResponses) && ep.errorResponses.length > 0 && (
                <div className="api__endpoint-section">
                    <h4 className="api__endpoint-sub">Errors</h4>
                    <ul className="api__errors">
                        {ep.errorResponses.map((e, i) => (
                            <li key={i}>
                                <Tag color="error">{e.code}</Tag>
                                <span>{e.description}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

/* -------------------------------------------------------------- */
/*  Sidebar                                                       */
/* -------------------------------------------------------------- */

function Sidebar({ groups, endpointsByGroup, activeId, onJump }) {
    return (
        <nav className="api__sidebar">
            {groups.map((g) => {
                const items = endpointsByGroup[g.id] || [];
                if (!items.length) return null;
                return (
                    <div key={g.id} className="api__group">
                        <div className="api__group-title">{g.name}</div>
                        <ul>
                            {items.map((ep) => (
                                <li key={ep.id}>
                                    <a
                                        href={`#${ep.id}`}
                                        className={activeId === ep.id ? 'is-active' : ''}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            onJump(ep.id);
                                        }}
                                    >
                                        {ep.title}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                );
            })}
        </nav>
    );
}

/* -------------------------------------------------------------- */
/*  Page                                                          */
/* -------------------------------------------------------------- */

export default function ApiDocs() {
    const intl = useIntl();
    const [query, setQuery] = useState('');
    const [activeId, setActiveId] = useState(null);

    const endpointsByGroup = useMemo(() => {
        const map = {};
        endpoints.forEach((ep) => {
            if (!map[ep.group]) map[ep.group] = [];
            map[ep.group].push(ep);
        });
        return map;
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return endpoints;

        return endpoints.filter((ep) => {
            const hay = [
                ep.title,
                ep.description,
                ep.path,
                ep.method,
                ep.type,
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            return hay.includes(q);
        });
    }, [query]);

    const grouped = useMemo(() => {
        const map = {};
        filtered.forEach((ep) => {
            if (!map[ep.group]) map[ep.group] = [];
            map[ep.group].push(ep);
        });
        return map;
    }, [filtered]);

    const onJump = (id) => {
        setActiveId(id);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    /* Auto-highlight the endpoint currently in view */
    useEffect(() => {
        const obs = new IntersectionObserver(
            (entries) => {
                const visible = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
                if (visible) setActiveId(visible.target.id);
            },
            { rootMargin: '-96px 0px -60% 0px', threshold: 0 },
        );
        document.querySelectorAll('.api__endpoint').forEach((el) => obs.observe(el));
        return () => obs.disconnect();
    }, [filtered]);

    const total = endpoints.filter((e) => e.type !== 'documentation').length;

    return (
        <>
            <ApiNav />

            <div className="api">
                {/* Hero */}
                <div className="api__hero">
                    <div className="api__hero-inner">
                        <div className="api__hero-icon">
                            <ApiOutlined />
                        </div>
                        <div>
                            <h1>
                                <FormattedMessage id="api.title" defaultMessage="Merchant API" />
                            </h1>
                            <p>
                                <FormattedMessage
                                    id="api.sub"
                                    defaultMessage="Integrate Sohocard into your platform. All endpoints are served through edge functions."
                                />
                            </p>
                            <Space size={8} wrap style={{ marginTop: 12 }}>
                                <Tag icon={<SafetyCertificateOutlined />} color="green">
                                    <FormattedMessage id="api.tag.secure" defaultMessage="HTTPS · x-api-key · JWT" />
                                </Tag>
                                <Tag color="blue">
                                    {total}{' '}
                                    <FormattedMessage id="api.tag.endpoints" defaultMessage="endpoints" />
                                </Tag>
                            </Space>
                        </div>
                    </div>
                </div>

                {/* Toolbar */}
                <div className="api__toolbar">
                    <Input
                        allowClear
                        size="large"
                        prefix={<SearchOutlined />}
                        placeholder={intl.formatMessage({
                            id: 'api.search.placeholder',
                            defaultMessage: 'Search endpoints…',
                        })}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                    <Button
                        size="large"
                        icon={<ReloadOutlined />}
                        onClick={() => setQuery('')}
                    >
                        <FormattedMessage id="api.reset" defaultMessage="Reset" />
                    </Button>
                </div>

                {filtered.length === 0 ? (
                    <div className="api__empty">
                        <Empty
                            description={intl.formatMessage({
                                id: 'api.empty',
                                defaultMessage: 'No endpoints match your search.',
                            })}
                        />
                    </div>
                ) : (
                    <div className="api__layout">
                        <Sidebar
                            groups={endpointGroups}
                            endpointsByGroup={grouped}
                            activeId={activeId}
                            onJump={onJump}
                        />

                        <main className="api__content">
                            {endpointGroups.map((g) => {
                                const items = grouped[g.id] || [];
                                if (!items.length) return null;
                                return (
                                    <section key={g.id} className="api__section">
                                        <h2 className="api__section-title">{g.name}</h2>
                                        {g.description && (
                                            <p className="api__section-desc">{g.description}</p>
                                        )}
                                        {items.map((ep) => (
                                            <EndpointCard key={ep.id} ep={ep} intl={intl} />
                                        ))}
                                    </section>
                                );
                            })}
                        </main>
                    </div>
                )}
            </div>
        </>
    );
}